import type { TFunction } from "i18next";

import type {
  IExportColumn,
  IExportRow,
  IExportSpec,
} from "@krea/shared/utils/exportToXlsx";

import type { IEarlyRepaymentPlan } from "./earlyRepayment";
import type { IRepaymentPlanBidInfo, IRepaymentPlanEntryData } from "./types";
import {
  type IRepaymentPlanDisplay,
  MONEY_COLUMNS,
} from "./useRepaymentPlanDisplay";

type ExportRow = Record<string, number | string>;

export const buildRepaymentPlanExportSpec = ({
  bid,
  companyName,
  loanApplicationId,
  lenderName,
  earlyRepaymentFeeLabel,
  earlyRepaymentTermsLabel,
  earlyRepaymentComment,
  hasNoCostReduction,
  formatCurrency,
  formatPaybackTime,
  t,
  displayPlan,
  totalValue,
  extendedMonths,
}: {
  bid: IRepaymentPlanBidInfo;
  companyName: string | null;
  loanApplicationId: string | null;
  lenderName: string | null;
  /** The terms in full, e.g. "1% of the remaining principal". Used in the
   * sheet header, where nothing else states them. */
  earlyRepaymentFeeLabel: string;
  /** The terms as shown in the savings block, which already carries the
   * amount charged. Identical to `earlyRepaymentFeeLabel` except for a
   * fixed-amount FEE_APPLIES, where the terms ARE that figure and the type
   * name is shown instead. */
  earlyRepaymentTermsLabel: string;
  /** Exported as its own row, never concatenated onto the fee label. */
  earlyRepaymentComment: string | null;
  /** FULL_INTEREST_AND_FEES: settling early saves nothing by contract, so the
   * savings section is replaced by the fee and the explanation. Mirrors the
   * on-screen panel deliberately — the two diverging is what let an
   * "interest saved" figure survive on one surface after the other dropped
   * it. */
  hasNoCostReduction: boolean;
  formatCurrency: (value: string | number) => string;
  formatPaybackTime: (months: number) => string;
  t: TFunction;
  displayPlan: IEarlyRepaymentPlan;
  totalValue: IRepaymentPlanDisplay["totalValue"];
  /** KREA-4474: set when a simulated longer term is in view. The filename
   * names the lender and application, so an unmarked export of a simulated
   * term would read as a real offer. */
  extendedMonths?: number | null;
}): IExportSpec<ExportRow> => {
  const setupFeeLabel =
    bid.setupFee && bid.setupFee > 0
      ? formatCurrency(bid.setupFee)
      : t("bids.common.repayableWithoutFee");

  const titleRows = [
    ...(companyName ? [companyName] : []),
    `${t("bids.companyLoanBid.amount")}: ${formatCurrency(bid.amount)}`,
    `${t("bids.companyLoanBid.paybackTimeMonths")}: ${formatPaybackTime(bid.paybackTimeMonths)}`,
    `${t("bids.companyLoanBid.amortizationMethod")}: ${bid.amortizationMethodDisplayName}`,
    `${t("bids.companyLoanBid.setupFee")}: ${setupFeeLabel}`,
    `${t("bids.companyLoanBid.earlyRepaymentFee")}: ${earlyRepaymentFeeLabel}`,
    ...(earlyRepaymentComment
      ? [`${t("bids.common.lendersComment")}: ${earlyRepaymentComment}`]
      : []),
  ];

  // KREA-4474: titleRows is string[] (see IExportSheet), not a row-shaped
  // object — the other entries above are plain strings too.
  if (extendedMonths) {
    titleRows.unshift(t("extended.exportNotice"));
  }

  const columns: IExportColumn<ExportRow>[] = [
    { header: t("month"), accessor: "month", width: 24, alignment: "left" },
    ...MONEY_COLUMNS.map((key) => ({
      header: t(key),
      accessor: key,
      width: 18,
      numberFormat: "#,##0.00",
      alignment: "right" as const,
    })),
  ];

  const valueKey = MONEY_COLUMNS[0] ?? "payment";
  const labelled = (
    label: string,
    value: number | string,
  ): IExportRow<ExportRow> => ({ data: { month: label, [valueKey]: value } });
  const spacer: IExportRow<ExportRow> = { data: { month: "" } };

  const rows: IExportRow<ExportRow>[] = displayPlan.entries.map(
    (entry: IRepaymentPlanEntryData) => {
      const data: ExportRow = { month: entry.month };
      for (const key of MONEY_COLUMNS) data[key] = Number(entry[key]);
      return { data };
    },
  );

  const totalData: ExportRow = { month: t("total") };
  for (const key of MONEY_COLUMNS) {
    const total = totalValue(key);
    totalData[key] = total === null ? "" : total;
  }
  rows.push({ data: totalData, isSummary: true });

  rows.push(spacer, { data: { month: t("totalsTitle") }, isSummary: true });
  rows.push(labelled(t("setupFee"), Number(displayPlan.totals.setupFee)));
  rows.push(
    labelled(
      t("totalCostOfCredit"),
      Number(displayPlan.totals.totalCostOfCredit),
    ),
  );
  rows.push(labelled(t("totalRepaid"), Number(displayPlan.totals.totalRepaid)));
  // The same fee the table marks: carried only by a truncated plan's balloon
  // row, so the full term exports none. `!== 0` matches the table too: a
  // "0" row would claim a charge exists.
  const { settlementFee } = displayPlan;
  if (settlementFee !== null && settlementFee !== 0) {
    rows.push(labelled(t("earlySettlementFee"), settlementFee));
  }

  if (displayPlan.isEarly) {
    // No savings heading and no avoided/net figures when the terms are "all
    // costs apply": they are arithmetically true and jointly meaningless, each
    // avoided krona being charged straight back as the fee. The fee itself is
    // already a row above; what is left to say is the explanation.
    if (!hasNoCostReduction) {
      rows.push(spacer, {
        data: { month: t("savings.title") },
        isSummary: true,
      });
      rows.push(
        labelled(t("savings.interestSaved"), displayPlan.interestSaved),
      );
      // The net only once the fee is known; without it the interest saved
      // above is already the whole story.
      if (displayPlan.settlementFee !== null) {
        rows.push(labelled(t("savings.netSaved"), displayPlan.netSaved));
      }
    } else {
      rows.push(spacer);
    }
    rows.push(
      labelled(t("savings.earlyRepaymentFee"), earlyRepaymentTermsLabel),
    );
    if (earlyRepaymentComment) {
      rows.push(
        labelled(t("bids.common.lendersComment"), earlyRepaymentComment),
      );
    }
  }

  // Slug filename segments so spaces/punctuation in the lender name don't
  // collide with the underscore-delimited pattern or break OS file handling.
  const slug = (value: string) =>
    value
      .trim()
      .replace(/[^\w]+/g, "-")
      .replace(/^-+|-+$/g, "");
  const simulated = extendedMonths ? `_simulated-${extendedMonths}m` : "";
  const filename = `amortisationplan_${loanApplicationId ?? "unknown"}_offer_${
    lenderName ? slug(lenderName) : "lender"
  }_${bid.id}${simulated}.xlsx`;

  return {
    filename,
    sheets: [{ sheetName: "Amortisation plan", titleRows, columns, rows }],
  };
};
